<template>
  <el-drawer
    v-model="visible"
    title="历史会话"
    :size="drawerSize"
    append-to-body
    @open="loadList(1)"
  >
    <div v-loading="loading" class="conv-list">
      <div v-if="!loading && list.length === 0" class="conv-empty">暂无历史会话</div>
      <div
        v-for="c in list"
        :key="c.id"
        class="conv-item"
        :class="{ active: c.id === currentId }"
        @click="select(c)"
      >
        <div class="conv-main">
          <div class="conv-title">{{ c.title || '未命名会话' }}</div>
          <div class="conv-meta">{{ formatTime(c.updatedAt) }} · {{ c.messageCount }} 条</div>
        </div>
        <el-icon class="conv-del" title="删除会话" @click.stop="remove(c)"><Delete /></el-icon>
      </div>
      <div v-if="total > list.length" class="conv-more" @click="loadList(page + 1)">加载更多</div>
    </div>
  </el-drawer>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Delete } from '@element-plus/icons-vue'
import {
  getAiConversations, getAiConversationDetail, deleteAiConversation,
  type AiConversation
} from '@/api/ai'

const props = defineProps<{
  modelValue: boolean
  currentId: number | null
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void
  (e: 'select', id: number, messages: { role: 'user' | 'assistant'; content: string }[]): void
  (e: 'deleted', id: number): void
}>()

const visible = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v)
})

// 窄屏（移动端）抽屉加宽
const drawerSize = computed(() => (window.innerWidth < 500 ? '85%' : '340px'))

const list = ref<AiConversation[]>([])
const page = ref(1)
const total = ref(0)
const loading = ref(false)

const loadList = async (p: number) => {
  loading.value = true
  try {
    const res = await getAiConversations({ page: p, pageSize: 20 })
    list.value = p === 1 ? res.list : [...list.value, ...res.list]
    total.value = res.pagination.total
    page.value = p
  } catch {
    // 拦截器已提示
  } finally {
    loading.value = false
  }
}

const select = async (c: AiConversation) => {
  try {
    const detail = await getAiConversationDetail(c.id)
    const messages = detail.messages
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .map(m => ({ role: m.role as 'user' | 'assistant', content: m.content }))
    emit('select', c.id, messages)
    visible.value = false
  } catch {
    // 拦截器已提示
  }
}

const remove = async (c: AiConversation) => {
  try {
    await ElMessageBox.confirm(`确定删除会话「${c.title || '未命名会话'}」？删除后不可恢复`, '删除会话', {
      type: 'warning',
      confirmButtonText: '删除',
      cancelButtonText: '取消'
    })
  } catch {
    return
  }
  try {
    await deleteAiConversation(c.id)
    ElMessage.success('会话已删除')
    emit('deleted', c.id)
    loadList(1)
  } catch {
    // 拦截器已提示
  }
}

const formatTime = (t: string) => (t ? String(t).replace('T', ' ').slice(5, 16) : '-')
</script>

<style scoped>
.conv-list { display: flex; flex-direction: column; gap: 8px; }
.conv-empty { text-align: center; color: #909399; padding: 40px 0; font-size: 13px; }
.conv-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}
.conv-item:hover { border-color: #409eff; }
.conv-item.active { border-color: #409eff; background: #ecf5ff; }
.conv-main { flex: 1; min-width: 0; }
.conv-title {
  font-size: 14px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.conv-meta { font-size: 12px; color: #909399; margin-top: 2px; }
.conv-del { color: #909399; flex-shrink: 0; padding: 4px; }
.conv-del:hover { color: #f56c6c; }
.conv-more { text-align: center; color: #409eff; font-size: 13px; cursor: pointer; padding: 8px 0; }
</style>
